/**
 * ErrorBoundary — funnel-wired React error boundary (EPIC #5310).
 *
 * Catches render-time errors in its subtree, reports them through the shared
 * client funnel (`reportClientError`), and shows a fallback that surfaces the
 * user-facing `EB-…` reference. Pair it with `installClientErrorTelemetry()`
 * (which catches uncaught/global errors + same-origin /api 5xx) for full
 * coverage: global handlers catch what escapes React, boundaries catch render
 * crashes before they blank the page.
 *
 * `variant` drives funnel dedup/severity:
 *   - 'app'    — wrap the whole tree; a full-page crash (bypasses dedup).
 *   - 'page'   — wrap a route/page; isolates a page crash.
 *   - 'inline' — wrap a risky widget (markdown, embeds, complex forms).
 *
 * Apps with their own branded boundary (e.g. www-sbi) can instead just call
 * `reportClientError` from their existing `componentDidCatch`; this component is
 * for surfaces that have none.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from './chunk-reload';
import { reportClientError, makeErrorCode, type ClientErrorVariant } from './error-telemetry';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Telemetry component label (e.g. "DashboardPage", "App"). Required for useful grouping. */
  component: string;
  /** Funnel severity/scope. Default 'inline'. */
  variant?: Extract<ClientErrorVariant, 'app' | 'page' | 'inline'>;
  /** Custom fallback UI. If omitted, a default card with the EB- ref + retry is shown. */
  fallback?: ReactNode;
  /** Also called when an error is caught (after the funnel report). */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Changing this resets the boundary (e.g. route key) so navigation clears the error. */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorRef: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorRef: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorCode = makeErrorCode();
    this.setState({ errorRef: errorCode.errorRef });

    reportClientError({
      component: this.props.component,
      message: error.message,
      stack: error.stack ?? null,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...errorCode,
      variant: this.props.variant ?? 'inline',
      errorType: error.name ?? null,
    });

    this.props.onError?.(error, errorInfo);
    console.error(`[ErrorBoundary:${this.props.component}]`, error, errorInfo);
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (prev.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, errorRef: null });
    }
  }

  handleRetry = (): void => {
    // #14194: a stale lazy chunk (after a deploy) fails again on a re-render; only a full reload
    // fetches the new chunk. The person asked for it, so this reload needs no guard.
    if (isChunkLoadError(this.state.error) && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, errorRef: null });
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="m-4 rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center">
        <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          This section failed to load. You can try again, or reload the page.
        </p>
        {this.state.errorRef && (
          <p className="mt-3 text-xs text-muted-foreground">
            Reference: <code className="font-mono">{this.state.errorRef}</code>
          </p>
        )}
        <button
          type="button"
          onClick={this.handleRetry}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Try Again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
